import { useEffect, useId, useRef, useState } from 'react'
import { CheckIcon, ChevronsUpDownIcon } from '@/shared/ui/icons'
import styles from './SelectFilter.module.css'

export type SelectOption<T extends string | number> = {
  value: T
  label: string
}

type SelectFilterProps<T extends string | number> = {
  /** Names the group, e.g. "Timeframe:". */
  label: string
  options: SelectOption<T>[]
  value: T
  onChange: (value: T) => void
  /** Hide the visible label (it still names the group). */
  hideLabel?: boolean
  /** A plain chip, without the up/down chevron (the design's range chip). */
  plain?: boolean
  /** Only one thing to choose: a pill that names it, not a control. */
  readOnly?: boolean
  /** Which edge of the chip the panel lines up with; `end` opens leftwards. */
  align?: 'start' | 'end'
}

/**
 * A labelled dropdown of one choice: a chip
 * opening a panel of radio rows, the chosen one checked. Radix's menu can't be
 * styled to match, so this is our own.
 */
export const SelectFilter = <T extends string | number>({
  label,
  options,
  value,
  onChange,
  hideLabel,
  plain,
  readOnly,
  align = 'start',
}: SelectFilterProps<T>) => {
  const labelId = useId()
  const name = useId()
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const selected = options.find(option => option.value === value)

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      setOpen(false)
      triggerRef.current?.focus()
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  return (
    <div className={styles.group} role='group' aria-labelledby={labelId}>
      <span
        id={labelId}
        className={hideLabel ? styles.hiddenLabel : styles.label}
      >
        {label}
      </span>
      <div ref={rootRef} className={styles.root}>
        <button
          ref={triggerRef}
          type='button'
          className={`${styles.trigger} ${plain ? styles.plain : ''}`}
          aria-haspopup='true'
          aria-expanded={open}
          disabled={readOnly}
          onClick={() => setOpen(current => !current)}
        >
          {selected?.label ?? value}
          {!plain && <ChevronsUpDownIcon />}
        </button>
        {open && (
          <div
            className={`${styles.panel} ${align === 'end' ? styles.end : ''}`}
          >
            {options.map(option => (
              <label key={option.value} className={styles.option}>
                <input
                  type='radio'
                  className={styles.input}
                  name={name}
                  value={option.value}
                  checked={option.value === value}
                  onChange={() => onChange(option.value)}
                  // Also fires for the option already chosen, which `onChange` doesn't.
                  onClick={() => setOpen(false)}
                />
                <span>{option.label}</span>
                <CheckIcon className={styles.check} />
              </label>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
